/**
 * BusinessBlockchain — recibir y enviar dólares por blockchain, para la EMPRESA.
 *
 * Misma capacidad que tiene el cliente persona, pero con una diferencia que
 * importa: la dirección y el saldo salen de un endpoint que resuelve el vault
 * POR LA EMPRESA, no por el usuario logueado. Así un operador nunca ve ni mueve
 * su billetera personal creyendo que es la de la compañía.
 *
 * El ENVÍO usa el mismo riel probado de personas (SMS + transferAssetToInternal
 * User con type 'external'), que es donde vive el money-out guard. No se
 * construye un segundo camino para sacar plata.
 *
 * Visual: .bc-ola + tokens --fx-* (glass Ola A, claro + oscuro). Solo chrome.
 */
import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import API from '../../lib/api';
import { getBlockchainBusiness } from '../../lib/cerebro';

const usd = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ASSET_ID = 'SOL_USDC_PTHX';

export default function BusinessBlockchain() {
  const [est, setEst] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('recibir');
  const [copiado, setCopiado] = useState(false);

  // Enviar
  const [monto, setMonto] = useState('');
  const [destino, setDestino] = useState('');
  const [paso, setPaso] = useState('form'); // form | codigo | listo
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState('');
  const destinoRef = useRef(null);

  const cargar = () => getBlockchainBusiness().then(setEst)
    .catch((e) => setErr(e?.response?.data?.message || 'No pude leer la billetera de la empresa.'));
  useEffect(() => { cargar(); }, []);

  if (err) return <div className="bc-ola"><div className="bc-ola__err">{err}</div></div>;
  if (!est) return <div className="bc-ola"><div className="bc-ola__loading">Cargando la billetera de la empresa…</div></div>;

  const n = Number(monto) || 0;
  const comision = Math.round(n * (est.comisionPct / 100) * 100) / 100;
  const totalDebita = Math.round((n + comision) * 100) / 100;
  const alcanza = totalDebita <= est.saldoEnCadena;
  const destinoValido = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(destino.trim()); // base58 Solana

  function copiar() {
    navigator.clipboard?.writeText(est.direccion);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  async function pedirCodigo() {
    setMsg(''); setEnviando(true);
    try {
      await API.post('auth/sendCodeToPhone', {});
      setPaso('codigo');
    } catch (e) {
      setMsg(e?.response?.data?.message || 'No pude enviarte el código.');
    } finally { setEnviando(false); }
  }

  async function confirmar() {
    setMsg(''); setEnviando(true);
    try {
      await API.post('auth/verifyCode', { code: codigo });
      const res = await API.post('/transaction/transferAssetToInternalUser', {
        assetId: ASSET_ID, amount: n, type: 'external', toAddress: destino.trim(),
      });
      if (String(res.data?.message || '').includes('successfully')) {
        setPaso('listo');
        setMonto(''); setDestino(''); setCodigo('');
        cargar();
      } else {
        setMsg(res.data?.message || 'No pude completar el envío.');
      }
    } catch (e) {
      // El guard responde 403 con code MONEY_OUT_LIMIT: se muestra tal cual,
      // que para eso el mensaje viene redactado para el cliente.
      setMsg(e?.response?.data?.message || 'No pude completar el envío.');
    } finally { setEnviando(false); }
  }

  return (
    <div className="bc-ola">
      <header className="bc-ola__head">
        <p className="bc-ola__eyebrow">Tesorería</p>
        <h1 className="bc-ola__title">Blockchain</h1>
        <p className="bc-ola__sub">
          Recibe y envía dólares por la red Solana desde la billetera de {est.empresa}.
        </p>
      </header>

      {est.sinVault ? (
        <div className="bc-ola__alert bc-ola__alert--warn">{est.mensaje}</div>
      ) : (
        <>
          {/* Saldos: DOS numeros, nunca uno solo que a veces miente */}
          <div className="bc-ola__metrics">
            <div className="bc-ola__metric">
              <div className="bc-ola__metric-lbl">Disponible en la red</div>
              <div className="bc-ola__metric-val">{usd(est.saldoEnCadena)}</div>
              <div className="bc-ola__metric-sub">Es lo que puedes enviar por blockchain.</div>
            </div>
            <div className="bc-ola__metric">
              <div className="bc-ola__metric-lbl">Saldo en FlowexPay</div>
              <div className="bc-ola__metric-val">{usd(est.saldoEnFlowexpay)}</div>
              <div className="bc-ola__metric-sub">El que usas para pagar y convertir dentro de la app.</div>
            </div>
          </div>

          <div className="bc-ola__tabs" role="tablist">
            {['recibir', 'enviar'].map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => { setTab(t); setPaso('form'); setMsg(''); }}
                className={`bc-ola__tab${tab === t ? ' bc-ola__tab--on' : ''}`}
              >
                {t === 'recibir' ? 'Recibir' : 'Enviar'}
              </button>
            ))}
          </div>

          {tab === 'recibir' && (
            <section className="bc-ola__panel">
              <h2 className="bc-ola__h2">Dirección de {est.empresa}</h2>
              <div className="bc-ola__qr-row">
                <div className="bc-ola__qr-frame">
                  <QRCodeCanvas value={est.direccion || ''} size={148} />
                </div>
                <div className="bc-ola__qr-body">
                  <div className="bc-ola__addr">{est.direccion}</div>
                  <button type="button" onClick={copiar} className="bc-ola__btn bc-ola__btn--primary">
                    {copiado ? '¡Copiada!' : 'Copiar dirección'}
                  </button>
                </div>
              </div>
              <div className="bc-ola__alert bc-ola__alert--bad">
                <strong>Envía solo USDC por la red Solana.</strong> Si mandas otra moneda
                o usas otra red, esos fondos se pierden y no hay forma de recuperarlos.
              </div>
              <p className="bc-ola__hint">
                Lo que llegue se acredita en el saldo de la empresa. Recibir no tiene costo;
                al convertir a pesos aplica la comisión de {est.comisionPct}%.
              </p>
            </section>
          )}

          {tab === 'enviar' && (
            <section className="bc-ola__panel">
              {!est.puedeEnviar ? (
                <div className="bc-ola__alert bc-ola__alert--warn">
                  Solo el dueño de la empresa puede enviar por blockchain. Si necesitas que
                  alguien más lo haga, hablémoslo: definimos quién puede y con qué aprobación.
                </div>
              ) : paso === 'listo' ? (
                <div className="bc-ola__ok-center">
                  <div className="bc-ola__ok-emoji" aria-hidden>✅</div>
                  <p className="bc-ola__ok-title">Envío procesado</p>
                  <p className="bc-ola__hint">Puede tardar unos minutos en confirmarse en la red.</p>
                  <button type="button" onClick={() => setPaso('form')} className="bc-ola__text-btn">
                    Hacer otro envío
                  </button>
                </div>
              ) : paso === 'codigo' ? (
                <div>
                  <h2 className="bc-ola__h2">Confirma con tu código</h2>
                  <p className="bc-ola__hint">
                    Te enviamos un código por SMS. Vas a enviar <strong>{usd(n)}</strong> y se
                    debitan <strong>{usd(totalDebita)}</strong> con la comisión.
                  </p>
                  <input
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    placeholder="000000"
                    className="bc-ola__input bc-ola__input--code"
                  />
                  <div className="bc-ola__actions">
                    <button
                      type="button"
                      onClick={confirmar}
                      disabled={codigo.length !== 6 || enviando}
                      className="bc-ola__btn bc-ola__btn--primary"
                    >
                      {enviando ? 'Enviando…' : 'Confirmar envío'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPaso('form'); setMsg(''); }}
                      className="bc-ola__btn bc-ola__btn--ghost"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="bc-ola__h2">Enviar a una dirección externa</h2>

                  <label className="bc-ola__label">Monto en dólares</label>
                  <input
                    value={monto}
                    onChange={(e) => setMonto(e.target.value.replace(/[^\d.]/g, ''))}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="bc-ola__input bc-ola__input--sm"
                  />

                  <label className="bc-ola__label">Dirección de destino (Solana)</label>
                  <input
                    ref={destinoRef}
                    value={destino}
                    onChange={(e) => setDestino(e.target.value.trim())}
                    placeholder="Pega aquí la dirección"
                    className="bc-ola__input bc-ola__input--mono"
                  />
                  {destino && !destinoValido && (
                    <p className="bc-ola__field-err">Esa no parece una dirección de Solana. Revísala.</p>
                  )}

                  {n > 0 && (
                    <div className="bc-ola__fee">
                      <div className="bc-ola__fee-row">
                        <span>Envías</span><span>{usd(n)}</span>
                      </div>
                      <div className="bc-ola__fee-row">
                        <span>Comisión ({est.comisionPct}%)</span><span>{usd(comision)}</span>
                      </div>
                      <div className="bc-ola__fee-row bc-ola__fee-row--total">
                        <span>Se debita de tu saldo</span><span>{usd(totalDebita)}</span>
                      </div>
                      {!alcanza && (
                        <p className="bc-ola__field-err">No te alcanza: disponible {usd(est.saldoEnCadena)}.</p>
                      )}
                    </div>
                  )}

                  <div className="bc-ola__alert bc-ola__alert--bad">
                    Un envío por blockchain <strong>no se puede reversar</strong>. Verifica la
                    dirección carácter por carácter antes de confirmar.
                  </div>

                  <button
                    type="button"
                    onClick={pedirCodigo}
                    disabled={!(n > 0 && destinoValido && alcanza) || enviando}
                    className="bc-ola__btn bc-ola__btn--primary bc-ola__btn--mt"
                  >
                    {enviando ? 'Enviando código…' : 'Continuar'}
                  </button>
                </div>
              )}
              {msg && <p className="bc-ola__msg">{msg}</p>}
            </section>
          )}
        </>
      )}
    </div>
  );
}
